import { NavLink, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

// Each tab carries the heading `title` + `description` shown for its page, so
// every sub-page gets its own heading block below the shared top tab strip
// (mirroring the Settings area). `title` can differ from the short tab `label`.
const adminTabs = [
  {
    label: "Home",
    path: "/admin",
    end: true,
    title: "Control Center",
    description:
      "Access every admin tool from one place — announcements, broadcasts, events, resources, media, files, and system settings.",
  },
  {
    label: "What's New",
    path: "/admin/whatsnew",
    end: false,
    title: "What's New",
    description: "Create and publish the popup announcement shown to all users at login, and browse or manage the archive of past entries.",
  },
  {
    label: "Broadcast",
    path: "/admin/broadcast",
    end: false,
    title: "Broadcast",
    description: "Send a live banner alert to all logged-in users, with a severity of danger, warning, or success.",
  },
  {
    label: "Events",
    path: "/admin/events",
    end: false,
    title: "Events",
    description: "Add or edit the news and events shown on every firm's dashboard feed.",
  },
  {
    label: "Resources",
    path: "/admin/resources",
    end: false,
    title: "Resources & IAN",
    description: "Manage reference links (optionally password-gated) and the IRS Advance Notice link list.",
  },
  {
    label: "Toolbox",
    path: "/admin/toolbox",
    end: false,
    title: "Marketing Toolbox",
    description: "Organize and edit marketing content across Social Media, Emails, Letters & Printables, and Handbooks.",
  },
  {
    label: "Videos",
    path: "/admin/videos",
    end: false,
    title: "Video Library",
    description: "Manage the video library across its sections, with thumbnail and embed-URL entry per video.",
  },
  {
    label: "Media",
    path: "/admin/media",
    end: false,
    title: "Media Library",
    description: "Build a nested media tree of folders, videos, PDFs, links, and text, and create symlinks to reuse assets across folders.",
  },
  {
    label: "Files",
    path: "/admin/files",
    end: false,
    title: "File Assets",
    description: "Upload and manage internal downloadable files by folder, and generate shareable URLs for referenced assets.",
  },
  {
    label: "Transcript Check",
    path: "/admin/transcheck",
    end: false,
    title: "Transcript Check",
    description: "View the latest transcript-parse regression result and its error count, and open the rules editor.",
  },
  {
    label: "WAIDS Map",
    path: "/admin/waidsmap",
    end: false,
    title: "WAIDS Map",
    description: "Maintain the mapping table that normalizes new-format IRS Wage & Income category text to the canonical older text.",
  },
  {
    label: "System",
    path: "/admin/settings",
    end: false,
    title: "System Settings",
    description: "Toggle IRS e-Services downtime status and set the banner message shown to users.",
  },
] as const;

function activeTab(pathname: string) {
  // Most-specific path first so a sub-path wins over the "/admin" home entry.
  return (
    [...adminTabs]
      .sort((a, b) => b.path.length - a.path.length)
      .find((t) =>
        t.end ? pathname === t.path : pathname === t.path || pathname.startsWith(`${t.path}/`),
      ) ?? adminTabs[0]
  );
}

/**
 * Shell for the vendor Control Center (`/admin/*`). Gated upstream by
 * `PlatformAdminRoute`. A sticky top tab strip (like the Settings area) plus a
 * per-page heading + description driven by the active tab.
 */
export function AdminLayout() {
  const tab = activeTab(useLocation().pathname);
  return (
    <div className="flex flex-col gap-6">
      <div className="bg-card -mx-5 -mt-6 min-w-0 border-b px-5 py-2.5 md:-mx-10 md:px-10 md:sticky md:top-0 md:z-10">
        <div className="flex flex-wrap items-center gap-1">
          {adminTabs.map((t) => (
            <NavLink
              key={t.path}
              to={t.path}
              end={t.end}
              className={({ isActive }) =>
                cn(
                  "flex h-9 shrink-0 items-center rounded-[10px] px-3.5 text-[13px] font-medium transition-colors",
                  isActive
                    ? "bg-brand-panel font-semibold text-white"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted",
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </div>
      </div>
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">{tab.title}</h1>
        <p className="text-muted-foreground text-sm">{tab.description}</p>
      </div>
      <div>
        <Outlet />
      </div>
    </div>
  );
}
